import React, { useMemo, useState, useEffect } from 'react';
import { useCreate, useDelete, useList, useOne } from '@refinedev/core';
import { Card, Col, Empty, Row, Typography, List, Button, Popconfirm } from 'antd';
import { SearchSelectField } from '@src/components/shared/fields/SearchSelectField';
import {
  DeleteOutlined,
  BulbOutlined,
  DollarCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

type OfferingLite = {
  id: number;
  name: string;
  price: number;
};

type PreviousInvoiceItem = {
  name: string;
  amount: number;
};

type PreviousInvoice = {
  id: string | number;
  createdAt?: string;
  items: PreviousInvoiceItem[];
  totalAmount: number;
};

/** Single treatment suggestion row (from API or derived) */
type SuggestionItem = {
  id: number;
  suggestionId: number;
  offeringId: number;
  name: string;
  price: number;
  appointment_id?: number | null;
  appointment?: { scheduled_datetime?: string } | null;
};

/** Group of suggestions by appointment (same structure as ClinicInfoTab) */
type SuggestionGroup = {
  key: string;
  title: string;
  subtitle?: string;
  items: SuggestionItem[];
  isCurrentAppointment: boolean;
};

function formatCurrency(amount: number) {
  const num = Number(amount) || 0;
  return `₹ ${num.toLocaleString('en-IN')}`;
}

function computeItemAmount(item: any): number {
  const finalPrice = Number(item?.final_price);
  if (Number.isFinite(finalPrice) && finalPrice > 0) return finalPrice;
  const unit = Number(item?.unit_price) || 0;
  const qty = Number(item?.quantity) || 1;
  return unit * qty;
}

export const useOfferingsInsights = ({
  appointmentId,
  enabled,
  showInvoices = true,
}: {
  appointmentId: string | number;
  enabled: boolean;
  showInvoices?: boolean;
}) => {
  const [shouldFetchAppointment, setShouldFetchAppointment] = useState(false);
  const [selectValue, setSelectValue] = useState<any>(null);
  const [treatmentQuickAddForGroup, setTreatmentQuickAddForGroup] = useState<string | null>(null);

  useEffect(() => {
    setShouldFetchAppointment(Boolean(enabled && appointmentId));
  }, [enabled, appointmentId]);

  // 1) Appointment (to get patient_id)
  const { data: appointmentData, isLoading: appointmentLoading } = useOne({
    resource: 'appointments',
    id: appointmentId,
    queryOptions: {
      enabled: shouldFetchAppointment,
    },
  });

  const appointment = appointmentData?.data;
  const patientId = appointment?.patient_id;

  // 2) Treatment suggestions: with patient_id → all for patient; without patient_id → current appointment only (stored by appointment_id only)
  const {
    data: suggestionsData,
    isLoading: suggestionsLoading,
    refetch: refetchSuggestions,
  } = useList({
    resource: 'treatment-suggestions',
    filters: patientId
      ? [{ field: 'patient_id', operator: 'eq' as const, value: patientId }]
      : [{ field: 'appointment_id', operator: 'eq' as const, value: appointmentId }],
    pagination: { mode: 'off' },
    queryOptions: {
      enabled: shouldFetchAppointment && !!appointmentId,
    },
  });

  // Build grouped suggestions (same layout/ordering as ClinicInfoTab): current appointment first, then date desc
  const groupedSuggestions = useMemo((): SuggestionGroup[] => {
    const raw = (suggestionsData?.data || []) as any[];
    if (!raw.length) return [];

    const groups = new Map<
      string,
      { title: string; subtitle?: string; items: SuggestionItem[]; isCurrent: boolean }
    >();

    for (const s of raw) {
      const apptId = s.appointment_id ?? s.appointment?.id ?? null;
      const key = apptId != null ? String(apptId) : 'no_appointment';
      const title =
        apptId != null ? `Appointment #${apptId}` : 'General Suggestions';
      const subtitle =
        apptId != null && s.appointment?.scheduled_datetime
          ? dayjs(s.appointment.scheduled_datetime).format('DD MMM, YYYY')
          : undefined;

      if (!groups.has(key)) {
        groups.set(key, {
          title,
          subtitle,
          items: [],
          isCurrent: Number(appointmentId) === (apptId ?? null),
        });
      }

      const offeringId = Number(s.offering_id ?? s.offering?.id);
      if (!offeringId) continue;

      const g = groups.get(key)!;
      // Prevent duplicate display: one row per offering per group (keep newest by suggestion id)
      const existingIdx = g.items.findIndex(item => item.offeringId === offeringId);
      const newItem = {
        id: offeringId,
        suggestionId: Number(s.id),
        offeringId,
        name: s.suggestion || s.offering?.name || 'Unnamed service',
        price: Number(s.price) || 0,
        appointment_id: apptId ?? undefined,
        appointment: s.appointment ?? undefined,
      };
      if (existingIdx >= 0) {
        if (Number(s.id) > (g.items[existingIdx].suggestionId ?? 0)) {
          g.items[existingIdx] = newItem;
        }
      } else {
        g.items.push(newItem);
      }
    }

    // Sort: current appointment group first, then others by date descending (newest first)
    const currentKey = String(appointmentId);
    const groupArray = Array.from(groups.entries()).map(([k, g]) => {
      const firstItem = g.items[0];
      const sortDate =
        firstItem?.appointment?.scheduled_datetime ?? null;
      return {
        key: k,
        title: g.title,
        subtitle: g.subtitle,
        items: g.items,
        isCurrentAppointment: g.isCurrent,
        sortDate,
      };
    });

    groupArray.sort((a, b) => {
      if (a.key === currentKey) return -1;
      if (b.key === currentKey) return 1;
      const ta = a.sortDate ? dayjs(a.sortDate).valueOf() : 0;
      const tb = b.sortDate ? dayjs(b.sortDate).valueOf() : 0;
      return tb - ta;
    });

    return groupArray.map(({ sortDate: _, ...rest }) => rest);
  }, [suggestionsData?.data, appointmentId]);

  // Flat list of current appointment's suggestions (for add duplicate check and total)
  const currentAppointmentSuggestions = useMemo(() => {
    const g = groupedSuggestions.find(gg => gg.isCurrentAppointment);
    return g?.items ?? [];
  }, [groupedSuggestions]);

  // Always ensure the current appointment block is visible:
  // - If there are no suggestions at all -> show the empty current appointment block
  // - If patient-based suggestions exist but none for the current appointment -> still show the empty current appointment block on top
  const displayGroups = useMemo((): SuggestionGroup[] => {
    const currentKey = String(appointmentId);
    let result: SuggestionGroup[];
    if (groupedSuggestions.length > 0) {
      result = [...groupedSuggestions];
      // If current appointment has no suggestions, add an empty "current" group on top
      if (!result.some(g => g.key === currentKey)) {
        const appt = appointment as any;
        result.unshift({
          key: currentKey,
          title: `Appointment #${appointmentId}`,
          subtitle: appt?.scheduled_datetime
            ? dayjs(appt.scheduled_datetime).format('DD MMM, YYYY')
            : undefined,
          items: [],
          isCurrentAppointment: true,
        });
      }
    } else {
      const appt = appointment as any;
      result = [
        {
          key: currentKey,
          title: `Appointment #${appointmentId}`,
          subtitle: appt?.scheduled_datetime
            ? dayjs(appt.scheduled_datetime).format('DD MMM, YYYY')
            : undefined,
          items: [],
          isCurrentAppointment: true,
        },
      ];
    }
    // If General Suggestions exist in data, keep them at the bottom (and only then show them)
    const generalIdx = result.findIndex(g => g.key === 'no_appointment');
    if (generalIdx >= 0) {
      const [general] = result.splice(generalIdx, 1);
      result.push(general);
    }
    return result;
  }, [groupedSuggestions, appointmentId, appointment]);

  // 2) Patient invoices (optional - can be disabled via showInvoices)
  const { data: invoicesData, isLoading: invoicesLoading } = useList({
    resource: 'clinic-invoices',
    filters: patientId
      ? [
          {
            field: 'patient_id',
            operator: 'eq',
            value: patientId,
          },
        ]
      : [],
    pagination: {
      mode: 'off',
    },
    queryOptions: {
      enabled: showInvoices && shouldFetchAppointment && !!patientId,
    },
  });

  // Mutations for treatment suggestions
  const { mutate: createSuggestion } = useCreate();
  const { mutate: deleteSuggestion } = useDelete();

  const previousOfferings = useMemo<{
    invoices: PreviousInvoice[];
    totalSpent: number;
  }>(() => {
    const invoices = showInvoices ? ((invoicesData?.data || []) as any[]) : [];
    let totalSpent = 0;

    const invoiceSummaries: PreviousInvoice[] = [];

    for (const invoice of invoices) {
      const invoiceId = invoice?.id;
      const createdAt = invoice?.created_at;
      const items = (invoice?.clinic_invoice_items || []) as any[];

      if (!items.length) continue;

      const mappedItems: PreviousInvoiceItem[] = [];
      let invoiceAmount = 0;

      for (const item of items) {
        const name = item?.offering?.name || item?.item_name || 'Unnamed service';
        const amount = computeItemAmount(item);
        invoiceAmount += amount;
        mappedItems.push({ name, amount });
      }

      totalSpent += invoiceAmount;
      invoiceSummaries.push({
        id: invoiceId,
        createdAt,
        items: mappedItems,
        totalAmount: invoiceAmount,
      });
    }

    // Newest invoices first
    invoiceSummaries.sort((a, b) => {
      const da = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
      const db = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
      return db - da;
    });

    return { invoices: invoiceSummaries, totalSpent };
  }, [invoicesData?.data]);

  const onSelectOffering = (val: any, targetGroupKey: string) => {
    setSelectValue(null);
    const offering = val?.data;
    if (!offering?.id) return;

    const offeringIdNum = Number(offering.id);
    const targetAppointmentId =
      targetGroupKey === 'no_appointment' ? null : Number(targetGroupKey);

    // Prevent duplicate: suggestion already exists for this appointment (or general) + offering
    const existingSuggestions = (suggestionsData?.data || []) as any[];
    const isDuplicate = existingSuggestions.some((s: any) => {
      const sOfferingId = Number(s.offering_id ?? s.offering?.id);
      if (sOfferingId !== offeringIdNum) return false;
      const sApptId = s.appointment_id ?? s.appointment?.id ?? null;
      const sApptNum = sApptId != null ? Number(sApptId) : null;
      if (targetAppointmentId == null) return sApptNum == null;
      return sApptNum === targetAppointmentId;
    });
    if (isDuplicate) return;

    createSuggestion(
      {
        resource: 'treatment-suggestions',
        values: {
          // When no patient_id: store with appointment_id only; when patient exists: include both
          ...(patientId != null && patientId !== '' && { patient_id: patientId }),
          ...(targetAppointmentId != null && { appointment_id: targetAppointmentId }),
          offering_id: offeringIdNum,
          suggestion: offering.name,
          price: Number(offering.price) || 0,
        },
      },
      {
        onSuccess: () => {
          setTreatmentQuickAddForGroup(null);
          refetchSuggestions?.();
        },
      }
    );
  };

  const removeSelectedOffering = (suggestionId: number) => {
    if (!suggestionId) return;
    deleteSuggestion(
      {
        resource: 'treatment-suggestions',
        id: suggestionId,
      },
      {
        onSuccess: () => {
          refetchSuggestions?.();
        },
      }
    );
  };

  const renderDetails = () => {
    if (!enabled || !appointmentId) return null;

    return (
      <div style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card
              style={{
                borderRadius: 12,
                border: '1px solid #e6f7ff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                height: 360,
              }}
              bodyStyle={{ padding: 16 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '999px',
                        backgroundColor: '#f5f5ff',
                        color: '#2f54eb',
                      }}
                    >
                      <BulbOutlined />
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 600, color: '#262626' }}>
                      Suggested Offerings
                    </span>
                  </div>
                </div>
              </div>

              {/* Grouped suggestions (same layout as ClinicInfoTab): per-group "Add Treatment Suggestion" for every appointment and General Suggestions */}
              <div
                style={{
                  marginTop: 12,
                  maxHeight: 300,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  paddingRight: 4,
                }}
              >
                {displayGroups.map(group => {
                  const isThisGroupAddOpen = treatmentQuickAddForGroup === group.key;
                  return (
                    <div
                      key={group.key}
                      style={{
                        marginBottom: 10,
                        paddingBottom: 10,
                        borderBottom:
                          group.key !==
                          displayGroups[displayGroups.length - 1]?.key
                            ? '1px solid #f0f0f0'
                            : undefined,
                      }}
                    >
                      <div
                        style={{
                          marginBottom: 4,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 8,
                        }}
                      >
                        <div>
                          <Text strong style={{ fontSize: 12, color: '#262626' }}>
                            {group.title}
                          </Text>
                          {group.subtitle && (
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                              {group.subtitle}
                            </div>
                          )}
                        </div>
                        {group.key !== 'no_appointment' && (
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setTreatmentQuickAddForGroup(group.key)}
                            className="offerings-insights-add-btn"
                            style={{
                              minWidth: 'auto',
                              flexShrink: 0,
                            }}
                            size="small"
                          >
                            Add Treatment Suggestion
                          </Button>
                        )}
                      </div>

                      {group.key !== 'no_appointment' && isThisGroupAddOpen && (
                        <div
                          style={{
                            marginBottom: 8,
                            padding: '6px 8px',
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <SearchSelectField
                            resource="offerings"
                            optionValue="id"
                            searchFields={['name', 'description', 'category']}
                            searchDebounceMs={300}
                            filters={[
                              {
                                field: 'category',
                                operator: 'in',
                                value: ['TREATMENT', 'LHR', 'PACKAGE', 'OTHER'],
                              },
                              {
                                field: 'is_active',
                                operator: 'in',
                                value: [1],
                              },
                            ]}
                            sorters={[{ field: 'name', order: 'asc' }]}
                            pagination={{ pageSize: 20 }}
                            queryOptions={{
                              enabled: true,
                              staleTime: 5 * 60 * 1000,
                              cacheTime: 10 * 60 * 1000,
                              refetchOnWindowFocus: false,
                              refetchOnMount: true,
                            }}
                            placeholder="Select offering..."
                            value={selectValue}
                            onChange={val => onSelectOffering(val, group.key)}
                            initialValue={null}
                            optionRender={(option: any) =>
                              `${option?.name || ''} (₹${option?.price ?? 0})`
                            }
                            allowClear
                            showSearch
                            style={{ width: '100%' }}
                          />
                          <Button
                            onClick={() => setTreatmentQuickAddForGroup(null)}
                            className="offerings-insights-add-btn"
                            size="small"
                            style={{ marginTop: 6 }}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}

                      <List
                        size="small"
                        bordered
                        dataSource={group.items}
                        rowKey="suggestionId"
                        renderItem={(item: SuggestionItem) => (
                          <List.Item
                            style={{ padding: '8px 12px' }}
                            actions={[
                              <Popconfirm
                                key="delete"
                                title="Are you sure you want to delete this item?"
                                onConfirm={() => removeSelectedOffering(item.suggestionId)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  style={{ padding: '2px 4px' }}
                                />
                              </Popconfirm>,
                            ]}
                          >
                            <div
                              style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  flex: 1,
                                  fontSize: 12,
                                  lineHeight: '16px',
                                  color: '#262626',
                                  fontWeight: 500,
                                }}
                              >
                                {item.name}
                              </div>
                              <div
                                style={{
                                  minWidth: 90,
                                  textAlign: 'right',
                                  fontWeight: 700,
                                  color: '#16a34a',
                                  fontSize: 12,
                                }}
                              >
                                {formatCurrency(item.price)}
                              </div>
                            </div>
                          </List.Item>
                        )}
                        style={{ backgroundColor: '#fff', marginBottom: 4 }}
                      />

                      {group.items.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: 4,
                            paddingTop: 4,
                            borderTop: '1px solid #f0f0f0',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#16a34a',
                          }}
                        >
                          Estimated total:{' '}
                          {formatCurrency(
                            group.items.reduce((s, i) => s + (Number(i.price) || 0), 0)
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {currentAppointmentSuggestions.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderTop: '1px solid #f0f0f0',
                      background: '#fff',
                      color: '#595959',
                      marginTop: 4,
                    }}
                  >
                    <div />
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {showInvoices && !!patientId && (
          <Col xs={24} md={12}>
            <Card
              style={{
                borderRadius: 12,
                border: '1px solid #f6ffed',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                height: 360,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        borderRadius: '999px',
                        backgroundColor: '#f6ffed',
                        color: '#52c41a',
                      }}
                    >
                      <DollarCircleOutlined />
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 600, color: '#262626' }}>
                      Previous Offerings
                    </span>
                  </div>
                  
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                {appointmentLoading || invoicesLoading ? (
                  <Text type="secondary">Loading previous offerings...</Text>
                ) : previousOfferings.invoices.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No past invoice items found."
                  />
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      maxHeight: 220,
                      overflow: 'auto',
                      paddingRight: 4,
                    }}
                  >
                    {previousOfferings.invoices.map(invoice => (
                      <div
                        key={invoice.id}
                        style={{
                          border: '1px solid #f0f0f0',
                          borderRadius: 12,
                          padding: '12px 12px',
                          background: '#fff',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 12,
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            {/* Invoice items list */}
                            {invoice.items.map((item, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom:
                                    idx < invoice.items.length - 1 ? 6 : 0,
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 500,
                                    color: '#262626',
                                    fontSize: 14,
                                  }}
                                >
                                  {item.name}
                                </div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: '#262626',
                                  }}
                                >
                                  {formatCurrency(item.amount)}
                                </div>
                              </div>
                            ))}

                            {/* Invoice meta (date + number) once per invoice */}
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: '#8c8c8c',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',               
                              }}
                            >

                              <div style={{ fontWeight: 500, color: '#262626', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <CalendarOutlined />
                              {invoice.createdAt
                                ? dayjs(invoice.createdAt).format('MMM DD, YYYY')
                                : '—'}
                              {invoice.id ? ( <><FileTextOutlined />INV-{invoice.id}</>) : ''}
                              </div>
                              <div style={{ fontWeight: 700, color: '#16a34a' }}>
                              {formatCurrency(invoice.totalAmount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#595959',
                }}
              >
                <div>{previousOfferings.invoices.length} invoices</div>
                <div style={{ fontWeight: 900, color: '#262626' }}>
                  Total Spent: {formatCurrency(previousOfferings.totalSpent)}
                </div>
              </div>
            </Card>
          </Col>
          )}
        </Row>
      </div>
    );
  };

  return {
    renderDetails,
  };
};

